(ns hai.home.desktop.mini-apps.tasks
  "My Tasks mini-app card: the same task data as the full Tasks page, grouped
   into collapsible Now / Next / Later sections. The header's looking glass opens
   the universal bar; the ＋ reveals a quick-add composer; the title links to the
   full Tasks page. The task rows and composer live here since the dashboard is
   their only home."
  (:require
   [hai.mini-apps.frame :as mini-apps]
   [hai.tasks.card :as task-card]
   [hai.tasks.data :as data]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.routing :as routing]
   [hai.web-app.universal-bar :as universal-bar]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

;; ---------------------------------------------------------------------------
;; Card frame
;; ---------------------------------------------------------------------------

(o/defstyled card mini-apps/card
  [:&::before {:left "auto" :right "40px"}]
  [:.app-header {:background t/--tasks}]
  ;; Match the navbar's Tasks icon (:task_alt) — a Material fill icon, unlike the
  ;; stroke icons the shared card frame assumes, so restore fill and its size.
  [:.app-icon [:svg {:fill "currentColor" :width "20px" :height "20px"}]]
  ;; Rows float as spaced cards rather than a bordered list, so the raised ones
  ;; read as pressable. The inset holds them off the section edges and gives the
  ;; drop shadows room.
  [:.section-tasks
   {:display "flex" :flex-direction "column" :gap "16px"
    :padding "4px 14px 16px"}]
  ;; The shared frame clips the last section to round the card's bottom; with
  ;; inset floating cards there is nothing to clip, and the clip would eat their
  ;; shadows and hover-lift. Let it overflow instead.
  [mini-apps/section
   [:&:last-child {:overflow "visible"}]])

;; ---------------------------------------------------------------------------
;; Panel
;; ---------------------------------------------------------------------------

(defn- section-view [{:keys [label tasks open? on-toggle]}]
  [mini-apps/section-view
   {:label label :item-count (count tasks) :open? open? :on-toggle on-toggle}
   (when (seq tasks)
     (into [:div.section-tasks]
           (map (fn [task]
                  ^{:key (:task/uuid task)}
                  [task-card/task-row-component task {:hover-actions? true :show-hint? true}]))
           tasks))])

(defn panel []
  (r/with-let [!adding? (r/atom false)
               !open (r/atom #{:now})
               toggle-section (fn [id]
                                (swap! !open (fn [open]
                                               (if (contains? open id)
                                                 (disj open id)
                                                 (conj open id)))))]
    (let [buckets @(<s [::data/buckets])
          loaded? @(<s [::data/loaded?])
          adding? @!adding?
          open @!open
          total (reduce + (map count (vals buckets)))]
      [card
       [:div.app-header
        [:a.app-title-link
         {:href (routing/href :tasks/view) :target routing/link-target}
         [:span.app-icon [icons/outlined-task_alt]]
         [:h2.app-title "My Tasks"]]
        [:div.app-actions
         [:button.app-action-btn
          {:type "button"
           :aria-label "Search tasks"
           :title "Search tasks"
           :on-click #(>e [::universal-bar/open])}
          [icons/outlined-search {:size 18}]]
         [:button.app-action-btn
          {:type "button"
           :aria-label "Add a task"
           :title "Add a task"
           :on-click #(reset! !adding? true)}
          [icons/plus {:size 16}]]]]
       (when adding?
         [task-card/quick-add-component {:on-close #(reset! !adding? false) :show-hint? true}])
       (cond
         (not loaded?) nil
         (zero? total) [:div.app-placeholder "Nothing yet — add your first task above."]
         :else
         (into [:div.task-sections]
               (map (fn [{:keys [id label]}]
                      ^{:key id}
                      [section-view {:label label
                                     :tasks (get buckets id)
                                     :open? (contains? open id)
                                     :on-toggle #(toggle-section id)}]))
               data/bucket-tabs))])))
